<template>
  <layout
    name="Clean"
    extraClass="builder-clean-layout"
    @titleupdate="updateTitleHandler"
  >
    <WithLoader :isLoading="!this.initialValues.mail_type">
      <div class="letters-templates-settings">
        <VLayoutHeader
          :name="name"
          :label="templateName"
          :actions="getActions"
          backBtn
          @save="newName => saveInputValue(newName)"
        ></VLayoutHeader>
        <!-- temporary hardcode uploaderType -->
        <TemplateSettingsSelect
          uploaderType="html"
          :template="template"
          type="letter"
          @upload-start="isFileLoading = true"
          @upload-finish="
            (file, file_type) => onFileUploadSuccess(file, file_type)
          "
          @changeType="
            type => {
              this.uploadType = type;
              this.saveTemplate();
            }
          "
          @savePdfUrl="saveTemplate"
          @saveHtml="onSaveHtml"
          extraClass="in-letters-setting"
        ></TemplateSettingsSelect>
        <div class="letters-thumbnails-wrapper">
          <VThumbnailsPreview
            :thumbnails="_getThumbnails"
            type="letter"
          ></VThumbnailsPreview>
        </div>
        <LettersBuilderSettingsForm
          :initialValues="initialValues"
          extraClass="letter-builder-form"
          @change="saveTemplate"
        ></LettersBuilderSettingsForm>
      </div>
    </WithLoader>
  </layout>
</template>

<script>
import Vue from "vue";
import { mapGetters, mapActions } from "vuex";

//components
import layout from "@/layouts/Layout";
import WithLoader from "@/components/WithLoader";
import TemplateSettingsSelect from "@/components/TemplateSettingsSelect";
import LettersBuilderSettingsForm from "@/components/LettersBuilderSettingsForm";
import VThumbnailsPreview from "@/components/VThumbnailsPreview";
import VLayoutHeader from "@/components/VLayoutHeader";
import VPreviewRenderedTemplate from "@/components/VPreviewRenderedTemplate";

//utils
import withFormUtils from "@/mixins/withFormUtils";
import withToasted from "@/mixins/withToasted";
import withNavigate from "@/mixins/withNavigate";
import { convertLetterToApiFormat } from "@/utils/util";

//assets
import inkitLogo from "@/assets/images/logo-w-container.svg";
import previewImageVertical from "@/assets/images/letters-preview-big.svg";

import { required } from "@/utils/validations";

export default Vue.component("LettersTemplatesSettings", {
  metaInfo: {
    title: "Template Builder"
  },

  components: {
    layout,
    WithLoader,
    LettersBuilderSettingsForm,
    TemplateSettingsSelect,
    VThumbnailsPreview,
    VLayoutHeader
  },

  mixins: [withFormUtils, withToasted, withNavigate],

  data() {
    const { id } = this.$route.params;

    return {
      id,
      template: null,
      name: "lettersBuilder",
      file: null,
      file_type: null,
      uploadType: "",
      initialValues: {},
      inkitLogo,
      previewImageVertical,
      isOpenInput: false,
      btnValue: "Edit",
      templateName: "",
      isFileLoading: false
    };
  },

  methods: {
    saveInputValue(newName) {
      this.templateName = newName;
      this.updateLettersTemplate({
        data: { id: this.id, name: this.templateName },
        onSuccess: () => {
          this.toast("Template name has been saved");
        },
        // to avoid updating loading state etc
        silent: true
      });
    },

    _cancel() {
      if (this.initialValues) {
        this.initializeForm({
          formName: this.name,
          fields: this.initialValues
        });
      }
    },

    saveTemplate() {
      let file_url = "";
      if (this.uploadType === "pdf") {
        if (this.file) {
          file_url = `https://f001.backblazeb2.com/b2api/v2/b2_download_file_by_id?fileId=${this.file}`;
        } else {
          file_url = this.getValue("file_url");
        }
      } else {
        file_url = this.template.file_url;
      }

      const objToSave = {
        ...convertLetterToApiFormat(this.formValues),
        // file_url,
        //TEMPORARY!!!
        // type: this.uploadType
        type: "html",
        file_url: null
      };

      if (!this.formValues.use_perforated) {
        objToSave.perforated = null;
      }

      // to properly save perforated value if it's not being set yet
      if (this.formValues.use_perforated && !objToSave.perforated) {
        objToSave.perforated = 1;
      }

      this.updateLettersTemplate({
        data: { id: this.id, ...objToSave },
        onSuccess: () => {
          this.toast("Template has been saved");
        },
        onError: () => {
          this.errorToast("Something went wrong");
        }
      });
    },

    updateTitleHandler(title) {
      this.updateLettersTemplate({
        id: this.template.id,
        name: title
      });
    },

    getInitialValues() {
      if (this.template) {
        const {
          double_sided,
          color,
          address_placement,
          perforated,
          return_envelope,
          mail_type,
          html,
          additional_service,
          type,
          product_code_id
        } = this.template;

        return {
          page_options: double_sided ? "double_sided" : "one_sided",
          color: color ? "color" : "b&w",
          blank_page: !(address_placement === "top_first_page"),
          use_perforated: !!perforated,
          perforated: { first: 1, last: perforated },
          mail_type,
          return_envelope: return_envelope ? return_envelope : false,
          additional_service,
          html: html || "<html></html>",
          product_code_id,
          type
        };
      }

      return {};
    },

    onFileUploadSuccess(file, file_type) {
      this.file = file;
      this.file_type = file_type;
      this.isFileLoading = false;
      this.saveTemplate();
    },

    onSaveHtml() {
      if (this.initialValues.html !== this.formValues.html) {
        this.saveTemplate();
      }
    },

    ...mapActions("lettersTemplates", [
      "updateLettersTemplate",
      "fetchTemplateById",
      "updatePreview"
    ])
  },

  computed: {
    getActions() {
      return [
        {
          component: VPreviewRenderedTemplate,
          props: { template: this.template, templateType: "letter" }
        }
      ];
    },

    formFields() {
      return [
        {
          name: "templateInput",
          elementType: "text",
          extraClass: "wide",
          validate: [required]
        }
      ];
    },

    _getThumbnails() {
      if (this.template) {
        let thumbnailsArr = this.template.thumbnails_urls;
        if (!thumbnailsArr) {
          thumbnailsArr = [previewImageVertical];
          return thumbnailsArr;
        }
        return thumbnailsArr;
      }

      return null;
    },

    isUrlField() {
      if (this.uploadType === "url") {
        return this.getValue("file_url", "fileUrlForm");
      }
      return true;
    },

    ...mapGetters("lettersTemplates", [
      "getTemplate",
      "templatesList",
      "isLoading"
    ])
  },

  created() {
    if (!this.id) {
      this.navigate("/letters-templates");
    }

    if (!this.isLoading) {
      this.fetchTemplateById({
        id: this.id
      }).then(res => this.template === res);
    }
    document.addEventListener("click", this.handleDocumentClick);
  },

  mounted() {
    // this.template = this.getTemplate(this.id).then(
    //   (this.uploadType = this.template.type)
    // );
    // this.uploadType = this.template.type;
    // this.onChangeSelectorType();
  },

  beforeDestroy() {
    document.removeEventListener("click", this.handleDocumentClick);
  },

  watch: {
    templatesList: function() {
      this.template = this.getTemplate(this.id);
    },

    template: function() {
      if (this.template) {
        this.initialValues = this.getInitialValues();
        this.templateName = this.template.name;
        //apply "html" type after creating template and template is empty yet
        this.uploadType = this.template.type || "html";
      }

      // const { file_url, html } = this.template;
      // let type = "url";
      //
      // if (!!html) {
      //   type = "html";
      // } else if (file_url && file_url.indexOf("backblaze") !== -1) {
      //   type = "pdf";
      // }
      //
      // this.uploadType = this.template.type;
    }
  }
});
</script>

<style lang="scss">
$ns: "letters-templates-settings";

.#{$ns} {
  width: 100%;
  padding-bottom: 30px;
  display: grid;
  grid-template-columns: minmax(350px, 1026px) auto;
  grid-template-areas:
    "header header"
    "selector settings"
    "thumbnails settings"
    ". settings";
  grid-gap: 50px;
  justify-content: center;
}

/*Grid areas*/
.v-template-builder-header {
  grid-area: header;
}
.template-settings-select {
  grid-area: selector;
}
.letters-builder-settings-form__wrapper {
  grid-area: settings;
}
.thumbnails-wrapper {
  grid-area: thumbnails;
}
/*=====*/

.#{$ns} /deep/ .template-settings-select {
  .template-settings-select__select {
    margin-bottom: 30px;
  }
}

.letters-thumbnails-wrapper {
  height: 270px;
  padding-left: 25px;
  overflow-x: auto;

  /deep/ .v-preview-image {
    &:last-child {
      padding-right: 25px;
    }
  }
}

.buttons-block {
  margin-top: 60px;
  display: flex;
  justify-content: flex-end;
  :first-child {
    margin-right: 30px;
  }
}

.custom-data__form-wrapper {
  overflow: auto;

  & /deep/ .v-input__label-wrapper {
    @include textCropEllipsis();

    label {
      flex-shrink: 1;
    }
  }
}

@media screen and (max-width: $medium-screen) {
  .#{$ns} {
    padding: 15px 50px;
    grid-template-columns: 1fr;
    grid-template-areas:
      "header"
      "selector"
      "thumbnails"
      "settings";
  }
}

/* extra class for templates selector */
.in-letters-setting {
  .template-settings-select__header {
    display: none;
  }

  .template-settings-select__select {
    display: none;
  }
}
</style>
